/**
 * Race Strategy Optimizer
 * 
 * Non-linear, per-stint strategy optimizer for endurance races.
 * Answers the question: "Should I push (12 stops), manage (11 stops), or save hard (10 stops)?"
 * by comparing the total race time of each stop count and picking the fastest.
 * 
 * Key features:
 *   - Compares all feasible stop counts side by side with concrete L/lap targets
 *   - Per-stint variable fuel/pace targets (not one flat % for the whole race)
 *   - Realistic: never suggests a lap time faster than the driver has demonstrated
 *   - Adaptive: detects when the driver can't hit targets and recalculates
 *   - Warmup-aware: ignores outlier laps (cold tires, formation lap, out-laps)
 * 
 * iRacing rules applied:
 *   - White flag rule: totalLaps = floor(raceDuration / lapTime) + 1
 *   - Multi-class: leader determines race end
 *   - No FCY/SC in iRacing IMSA — pure green flag racing
 *   - Full stop includes free tires; splash-and-go = no tires
 */

import type { CarClass, LiveStrategyInput } from './liveStrategyCalculator';

// ─── Types ──────────────────────────────────────────────────────────────

/** A single stint's targets within a race plan. */
export interface StintTarget {
  stintNumber: number;
  mode: 'push' | 'normal' | 'save';
  /** Target fuel consumption per lap (liters) — the concrete number the driver sees */
  targetFuelPerLap: number;
  /** Expected lap time at this fuel target (seconds) */
  expectedLapTime: number;
  /** Fuel save percentage vs normal pace (0 = push/normal, positive = saving) */
  fuelSavePercent: number;
  /** Number of laps in this stint */
  laps: number;
  /** Fuel at start of stint (liters) */
  fuelAtStart: number;
  /** Fuel at end of stint (liters) */
  fuelAtEnd: number;
  /** Pit type after this stint (null for last stint) */
  pitAfter: 'full' | 'splash' | null;
}

/** A complete race plan for a specific stop count. */
export interface StopCountPlan {
  /** Number of pit stops in this plan */
  stops: number;
  /** Total race time = driving + pitting (seconds) */
  totalRaceTime: number;
  /** Total driving time only (seconds) */
  drivingTime: number;
  /** Total pit time only (seconds) */
  pitTime: number;
  /** Total laps completed before checkered */
  totalLaps: number;
  /** Overall fuel save % needed (weighted average across all stints) */
  avgFuelSavePercent: number;
  /** Concrete target fuel per lap (weighted average — for the summary display) */
  avgTargetFuelPerLap: number;
  /** Expected average lap time (seconds) */
  avgExpectedLapTime: number;
  /** Whether this plan is feasible (save % within limits, fuel fits, etc.) */
  feasible: boolean;
  /** Why it's infeasible (empty string if feasible) */
  infeasibleReason: string;
  /** Per-stint targets */
  stints: StintTarget[];
  /** Time delta vs the best plan (0 = best, positive = slower) */
  timeDeltaVsBest: number;
  /** Short human-readable label, e.g. "11 stops, save 3.2% (3.31 L/lap)" */
  label: string;
}

/** The optimizer's full output: all plans compared + the recommended one. */
export interface StrategyComparison {
  /** All evaluated plans, sorted best (fastest) to worst */
  plans: StopCountPlan[];
  /** The recommended plan (fastest feasible) */
  recommended: StopCountPlan;
  /** Current stint's immediate instruction */
  currentAction: 'push' | 'normal' | 'save';
  /** Concrete fuel target for right now (L/lap) */
  currentTargetFuelPerLap: number;
  /** Expected lap time at the current target (seconds) */
  currentExpectedLapTime: number;
  /** The driver's proven best pace — we never suggest faster than this */
  driverBestLapTime: number;
  /** The driver's proven best fuel economy (lowest L/lap achieved consistently) */
  driverBestFuelPerLap: number;
  /** Whether the driver is meeting the current target */
  driverOnTarget: boolean;
  /** If not on target: what's the gap? */
  targetGap: TargetGap | null;
  /** How many clean laps the averages are based on (excluding warmup) */
  cleanLapCount: number;
}

/** Gap between target and actual driver performance. */
export interface TargetGap {
  /** Actual avg fuel per lap vs target (positive = using more than target) */
  fuelGapPerLap: number;
  /** Actual avg lap time vs expected (positive = slower than expected) */
  lapTimeGap: number;
  /** What this means for the plan */
  message: string;
  /** Should we recalculate with the driver's actual capabilities? */
  shouldRecalculate: boolean;
}

/** Lap data used for driver capability analysis. */
export interface CleanLapData {
  lapNumber: number;
  lapTime: number;
  fuelUsed: number;
}

// ─── Driver Capability Tracker ──────────────────────────────────────────

/**
 * Filters warmup/cold-tire/pit laps and builds a picture of what the driver
 * can actually achieve. Used to set realistic bounds on suggestions.
 */
export function analyzeDriverCapability(
  laps: Array<{ lapNumber: number; lapTime: number | null; fuelLevel: number | null }>,
  fuelCapacity: number,
): {
  cleanLaps: CleanLapData[];
  avgLapTime: number;
  avgFuelPerLap: number;
  bestLapTime: number;
  bestFuelPerLap: number;
  recentAvgLapTime: number;
  recentAvgFuelPerLap: number;
} {
  // Build fuel-used-per-lap from fuel level deltas
  const lapsWithFuel: CleanLapData[] = [];
  for (let i = 1; i < laps.length; i++) {
    const prev = laps[i - 1];
    const curr = laps[i];
    if (curr.lapTime === null || curr.lapTime <= 0) continue;
    if (prev.fuelLevel === null || curr.fuelLevel === null) continue;

    const fuelUsed = prev.fuelLevel - curr.fuelLevel;

    // Skip pit out-laps: fuel went UP (refueled) or fuel used is negative/tiny
    if (fuelUsed < 0.5) continue;

    // Skip laps with absurd fuel usage (> 50% of tank = data glitch or pit in-lap)
    if (fuelUsed > fuelCapacity * 0.5) continue;

    lapsWithFuel.push({
      lapNumber: curr.lapNumber,
      lapTime: curr.lapTime,
      fuelUsed,
    });
  }

  // Remove warmup laps: first 2 clean laps tend to be slow (cold tires, cautious)
  const cleanLaps = lapsWithFuel.slice(2);

  if (cleanLaps.length === 0) {
    // Not enough data — fall back to whatever we have
    const fallback = lapsWithFuel.length > 0 ? lapsWithFuel : [{
      lapNumber: 0, lapTime: 120, fuelUsed: 3.5,
    }];
    const avgLT = fallback.reduce((s, l) => s + l.lapTime, 0) / fallback.length;
    const avgFPL = fallback.reduce((s, l) => s + l.fuelUsed, 0) / fallback.length;
    return {
      cleanLaps: fallback,
      avgLapTime: avgLT,
      avgFuelPerLap: avgFPL,
      bestLapTime: Math.min(...fallback.map(l => l.lapTime)),
      bestFuelPerLap: Math.min(...fallback.map(l => l.fuelUsed)),
      recentAvgLapTime: avgLT,
      recentAvgFuelPerLap: avgFPL,
    };
  }

  // Remove outliers: laps > mean + 2σ (traffic, mistakes, slow zones)
  const meanLT = cleanLaps.reduce((s, l) => s + l.lapTime, 0) / cleanLaps.length;
  const stdLT = Math.sqrt(cleanLaps.reduce((s, l) => s + (l.lapTime - meanLT) ** 2, 0) / cleanLaps.length);
  const cutoff = meanLT + 2 * stdLT;
  const filtered = cleanLaps.filter(l => l.lapTime <= cutoff);

  const final = filtered.length >= 3 ? filtered : cleanLaps;

  const avgLapTime = final.reduce((s, l) => s + l.lapTime, 0) / final.length;
  const avgFuelPerLap = final.reduce((s, l) => s + l.fuelUsed, 0) / final.length;
  const bestLapTime = Math.min(...final.map(l => l.lapTime));
  const bestFuelPerLap = Math.min(...final.map(l => l.fuelUsed));

  // Recent averages (last 5 clean laps) — more responsive to current conditions
  const recent = final.slice(-5);
  const recentAvgLapTime = recent.reduce((s, l) => s + l.lapTime, 0) / recent.length;
  const recentAvgFuelPerLap = recent.reduce((s, l) => s + l.fuelUsed, 0) / recent.length;

  return {
    cleanLaps: final,
    avgLapTime,
    avgFuelPerLap,
    bestLapTime,
    bestFuelPerLap,
    recentAvgLapTime,
    recentAvgFuelPerLap,
  };
}

// ─── Core Optimizer ─────────────────────────────────────────────────────

/**
 * Class-specific constants — mirrors LiveStrategyCalculator's values.
 * Centralised here so the optimizer is self-contained.
 */
function getClassConstants(carClass?: CarClass) {
  switch (carClass) {
    case 'GTP':  return { maxSave: 15, fullPit: 55, splashPit: 30, penalty: 0.10 };
    case 'LMP2': return { maxSave: 12, fullPit: 55, splashPit: 30, penalty: 0.18 };
    case 'GT3':  return { maxSave: 15, fullPit: 62, splashPit: 35, penalty: 0.13 };
    default:     return { maxSave: 15, fullPit: 60, splashPit: 32, penalty: 0.15 };
  }
}

/**
 * Build a plan for a specific stop count by simulating the race timeline.
 * 
 * Approach: iterate over possible stint lengths (laps per stint) to find the
 * fuel-save % that maximizes total laps completed. Each stint length implies
 * a specific fuelPerLap target (fuelCapacity / stintLength), which maps to
 * a save % and a lap time penalty.
 * 
 * This avoids the integer-rounding fuel waste from the continuous binary search:
 * each stint uses exactly stintLength × fuelPerLap = tankCapacity fuel (zero waste).
 * 
 * The plan that completes the MOST laps wins. Tie-break by lower total race time.
 */
function buildPlanForStopCount(
  stops: number,
  input: LiveStrategyInput,
  raceTimeRemaining: number,
  driverAvgLapTime: number,
  driverAvgFuelPerLap: number,
  driverBestLapTime: number,
): StopCountPlan | null {

  const { maxSave, fullPit, splashPit, penalty } = getClassConstants(input.carClass);

  // ── Dynamic pit time calculation ──────────────────────────────────
  const hasDynamicPit = input.pitInTime !== undefined && input.pitOutTime !== undefined && input.refuelRate !== undefined;
  const pitIn = input.pitInTime ?? 0;
  const pitOut = input.pitOutTime ?? 0;
  const refuelRate = input.refuelRate ?? 0;

  function estimatePitTime(fuelToAdd: number, isSplash: boolean): number {
    if (hasDynamicPit) {
      return pitIn + fuelToAdd * refuelRate + pitOut;
    }
    return isSplash ? (input.pitStopSplashTime ?? splashPit) : (input.pitStopTime ?? fullPit);
  }

  if (raceTimeRemaining <= 0) return null;

  // Quick feasibility: can we fit all pit stops in the remaining time?
  const worstCasePitPerStop = hasDynamicPit
    ? (pitIn + input.fuelCapacity * refuelRate + pitOut)
    : (input.pitStopTime ?? fullPit);
  if (stops * worstCasePitPerStop >= raceTimeRemaining) {
    return {
      stops, totalRaceTime: Infinity, drivingTime: 0, pitTime: stops * worstCasePitPerStop,
      totalLaps: 0, avgFuelSavePercent: 0, avgTargetFuelPerLap: driverAvgFuelPerLap,
      avgExpectedLapTime: driverAvgLapTime, feasible: false,
      infeasibleReason: 'Pit time exceeds remaining race time.',
      stints: [], timeDeltaVsBest: 0, label: `${stops} stops — too much pit time`,
    };
  }

  // ── Try different stint lengths to find the one that maximizes laps ──
  // Stint length = number of laps per full tank. Determines fuelPerLap target.
  // Range: natural stint (0% save) up to maxSave%
  const naturalStintLaps = Math.floor(input.fuelCapacity / driverAvgFuelPerLap);
  const minFuelPerLap = driverAvgFuelPerLap * (1 - maxSave / 100);
  const maxStintLaps = minFuelPerLap > 0 ? Math.floor(input.fuelCapacity / minFuelPerLap) : naturalStintLaps;

  type SimResult = {
    stints: StintTarget[]; totalLaps: number; totalDrivingTime: number;
    totalPitTime: number; savePercent: number; fuelPerLap: number;
    lapTime: number; stopsUsed: number;
  };

  let bestResult: SimResult | null = null;

  for (let stintLaps = naturalStintLaps; stintLaps <= maxStintLaps; stintLaps++) {
    // Target fuel per lap so each full-tank stint uses exactly stintLaps laps
    const targetFuelPerLap = input.fuelCapacity / stintLaps;
    const savePercent = Math.max(0, ((driverAvgFuelPerLap - targetFuelPerLap) / driverAvgFuelPerLap) * 100);
    if (savePercent > maxSave) continue;

    const adjLapTime = Math.max(driverAvgLapTime + savePercent * penalty, driverBestLapTime);

    // ── Simulate the race stint by stint ──────────────────────────
    const stints: StintTarget[] = [];
    let timeUsed = 0;
    let fuel = input.currentFuel;
    let simTotalLaps = 0;
    let simDrivingTime = 0;
    let simPitTime = 0;
    let stopsUsed = 0;
    let stintNum = 0;

    while (timeUsed < raceTimeRemaining) {
      stintNum++;
      const stintMaxLaps = Math.floor(fuel / targetFuelPerLap);
      if (stintMaxLaps <= 0) break;

      let lapsThisStint = 0;
      const fuelAtStart = fuel;

      for (let lap = 0; lap < stintMaxLaps; lap++) {
        const timeRemaining = raceTimeRemaining - timeUsed;

        // White flag: remaining time < one lap → this is the final lap
        if (timeUsed > 0 && timeRemaining < adjLapTime && timeRemaining > 0) {
          if (fuel >= targetFuelPerLap) {
            timeUsed += adjLapTime;
            fuel -= targetFuelPerLap;
            lapsThisStint++;
            simTotalLaps++;
            simDrivingTime += adjLapTime;
          }
          timeUsed = raceTimeRemaining; // Race over
          break;
        }

        // Normal lap
        timeUsed += adjLapTime;
        fuel -= targetFuelPerLap;
        lapsThisStint++;
        simTotalLaps++;
        simDrivingTime += adjLapTime;

        if (timeUsed >= raceTimeRemaining) break;
      }

      const fuelAtEnd = Math.max(0, fuel);
      const raceOver = timeUsed >= raceTimeRemaining;

      // Pit stop?
      let pitAfter: StintTarget['pitAfter'] = null;
      if (!raceOver && stopsUsed < stops) {
        const isLastStop = stopsUsed === stops - 1;
        const fullFuelToAdd = input.fuelCapacity - fuelAtEnd;
        const fullPitDuration = estimatePitTime(fullFuelToAdd, false);
        const timeAfterFullPit = timeUsed + fullPitDuration;

        // Decide splash vs full
        if (isLastStop) {
          const lapsAfterPit = Math.floor((raceTimeRemaining - timeAfterFullPit) / adjLapTime) + 1;
          const fuelNeededAfterPit = lapsAfterPit * targetFuelPerLap;
          const fuelDeficit = fuelNeededAfterPit - fuelAtEnd;
          if (fuelDeficit > 0 && fuelDeficit < input.fuelCapacity * 0.5) {
            const splashAmount = Math.min(fuelDeficit + 1, input.fuelCapacity - fuelAtEnd);
            const splashDuration = estimatePitTime(splashAmount, true);
            pitAfter = 'splash';
            simPitTime += splashDuration;
            timeUsed += splashDuration;
            fuel = fuelAtEnd + splashAmount;
          } else {
            pitAfter = 'full';
            simPitTime += fullPitDuration;
            timeUsed += fullPitDuration;
            fuel = input.fuelCapacity;
          }
        } else {
          pitAfter = 'full';
          simPitTime += fullPitDuration;
          timeUsed += fullPitDuration;
          fuel = input.fuelCapacity;
        }
        stopsUsed++;
      }

      let mode: StintTarget['mode'] = 'normal';
      if (savePercent > 1.5) mode = 'save';

      stints.push({
        stintNumber: stintNum, mode, targetFuelPerLap, expectedLapTime: adjLapTime,
        fuelSavePercent: savePercent, laps: lapsThisStint,
        fuelAtStart, fuelAtEnd, pitAfter,
      });

      if (raceOver) break;
      if (pitAfter === null && fuel < targetFuelPerLap) break; // Out of fuel, no stops left
    }

    // Is this the best result for this stop count?
    const totalRaceTime = simDrivingTime + simPitTime;
    if (!bestResult ||
        simTotalLaps > bestResult.totalLaps ||
        (simTotalLaps === bestResult.totalLaps && totalRaceTime < bestResult.totalDrivingTime + bestResult.totalPitTime)) {
      bestResult = {
        stints, totalLaps: simTotalLaps, totalDrivingTime: simDrivingTime,
        totalPitTime: simPitTime, savePercent, fuelPerLap: targetFuelPerLap,
        lapTime: adjLapTime, stopsUsed,
      };
    }
  }

  if (!bestResult || bestResult.totalLaps === 0) {
    return {
      stops, totalRaceTime: Infinity, drivingTime: 0, pitTime: 0,
      totalLaps: 0, avgFuelSavePercent: 0, avgTargetFuelPerLap: driverAvgFuelPerLap,
      avgExpectedLapTime: driverAvgLapTime, feasible: false,
      infeasibleReason: `Cannot complete any laps with ${stops} stops.`,
      stints: [], timeDeltaVsBest: 0, label: `${stops} stops — infeasible`,
    };
  }

  // Check if save % exceeds max
  if (bestResult.savePercent > maxSave) {
    return {
      stops, totalRaceTime: Infinity, drivingTime: 0, pitTime: 0,
      totalLaps: 0, avgFuelSavePercent: bestResult.savePercent, avgTargetFuelPerLap: bestResult.fuelPerLap,
      avgExpectedLapTime: bestResult.lapTime, feasible: false,
      infeasibleReason: `Needs ${bestResult.savePercent.toFixed(1)}% saving (max ${maxSave}%).`,
      stints: [], timeDeltaVsBest: 0,
      label: `${stops} stop${stops !== 1 ? 's' : ''} — needs ${bestResult.savePercent.toFixed(1)}% save`,
    };
  }

  const saveLabel = bestResult.savePercent > 1
    ? `save ${bestResult.savePercent.toFixed(1)}% (${bestResult.fuelPerLap.toFixed(2)} L/lap)`
    : `normal pace (${bestResult.fuelPerLap.toFixed(2)} L/lap)`;

  return {
    stops: bestResult.stopsUsed,
    totalRaceTime: bestResult.totalDrivingTime + bestResult.totalPitTime,
    drivingTime: bestResult.totalDrivingTime,
    pitTime: bestResult.totalPitTime,
    totalLaps: bestResult.totalLaps,
    avgFuelSavePercent: bestResult.savePercent,
    avgTargetFuelPerLap: bestResult.fuelPerLap,
    avgExpectedLapTime: bestResult.lapTime,
    feasible: true,
    infeasibleReason: '',
    stints: bestResult.stints,
    timeDeltaVsBest: 0,
    label: `${bestResult.stopsUsed} stop${bestResult.stopsUsed !== 1 ? 's' : ''}, ${saveLabel}`,
  };
}

// ─── Target Gap Detection ───────────────────────────────────────────────

/**
 * Check if the driver is hitting the recommended targets.
 * Uses the last 3 clean laps to see if actual consumption/pace matches target.
 */
function detectTargetGap(
  recentAvgFuelPerLap: number,
  recentAvgLapTime: number,
  targetFuelPerLap: number,
  expectedLapTime: number,
): TargetGap | null {
  const fuelGap = recentAvgFuelPerLap - targetFuelPerLap;
  const lapTimeGap = recentAvgLapTime - expectedLapTime;

  // Tolerances: 0.05 L/lap for fuel, 0.3s for lap time
  const fuelOnTarget = Math.abs(fuelGap) < 0.05;
  const paceOnTarget = Math.abs(lapTimeGap) < 0.3;

  if (fuelOnTarget && paceOnTarget) return null;

  // Driver is NOT hitting targets
  let message = '';
  let shouldRecalculate = false;

  if (fuelGap > 0.05) {
    // Using more fuel than target
    message = `Using ${fuelGap.toFixed(2)} L/lap more than target (${recentAvgFuelPerLap.toFixed(2)} vs ${targetFuelPerLap.toFixed(2)}).`;
    if (fuelGap > 0.15) {
      message += ' Recalculating strategy with actual consumption.';
      shouldRecalculate = true;
    }
  } else if (lapTimeGap > 0.3 && fuelGap <= 0.05) {
    // Slower than expected but fuel is on target — just slower driver
    message = `Pace is ${lapTimeGap.toFixed(1)}s off target (${recentAvgLapTime.toFixed(2)}s vs ${expectedLapTime.toFixed(2)}s).`;
    if (lapTimeGap > 1.0) {
      message += ' Adjusting expectations to actual pace.';
      shouldRecalculate = true;
    }
  } else if (fuelGap < -0.05) {
    // Saving more than needed — might be able to push
    message = `Saving ${Math.abs(fuelGap).toFixed(2)} L/lap more than needed. You can push harder.`;
  }

  return { fuelGapPerLap: fuelGap, lapTimeGap: lapTimeGap, message, shouldRecalculate };
}

// ─── Main Entry Point ───────────────────────────────────────────────────

/**
 * Compare all feasible stop counts and build the full strategy recommendation.
 * 
 * This is the main function called every lap from the live telemetry page.
 * It replaces the flat "save X%" recommendation with a per-stint roadmap.
 */
export function optimizeStrategy(
  input: LiveStrategyInput,
  laps: Array<{ lapNumber: number; lapTime: number | null; fuelLevel: number | null }>,
  myTotalLaps: number,
): StrategyComparison | null {

  // ── Step 1: Analyze driver capability (filtered, clean data) ──────
  const driver = analyzeDriverCapability(laps, input.fuelCapacity);
  if (driver.cleanLaps.length < 1) return null; // Not enough data yet

  const lapsRemaining = Math.max(0, myTotalLaps - input.currentLap);
  if (lapsRemaining <= 0) return null;

  // Use recent averages (responsive to current conditions) for planning
  const driverAvgLapTime = driver.recentAvgLapTime;
  const driverAvgFuelPerLap = driver.recentAvgFuelPerLap;
  const driverBestLapTime = driver.bestLapTime;

  // ── Step 2: Determine range of stop counts to evaluate ────────────
  const maxStintLaps = Math.floor(input.fuelCapacity / driverAvgFuelPerLap);
  const minStops = Math.max(0, Math.ceil(lapsRemaining / maxStintLaps) - 1);
  // Evaluate -1, baseline, +1, +2 stops to find optimal
  const stopRange = [
    Math.max(0, minStops - 1),
    minStops,
    minStops + 1,
    minStops + 2,
  ];
  // Remove duplicates
  const uniqueStops = [...new Set(stopRange)].sort((a, b) => a - b);

  // ── Step 3: Build a plan for each stop count ──────────────────────
  // Convert lapsRemaining to time remaining for the race simulation
  const raceTimeRemaining = lapsRemaining * driverAvgLapTime;

  const plans: StopCountPlan[] = [];
  for (const stops of uniqueStops) {
    const plan = buildPlanForStopCount(
      stops, input, raceTimeRemaining,
      driverAvgLapTime, driverAvgFuelPerLap, driverBestLapTime,
    );
    if (plan) plans.push(plan);
  }

  // Deduplicate plans that end up with the same actual stop count
  // (e.g. asking for 13 stops may only use 12 — same as the 12-stop plan)
  const deduped: StopCountPlan[] = [];
  const seenStops = new Set<number>();
  for (const plan of plans) {
    if (!seenStops.has(plan.stops)) {
      seenStops.add(plan.stops);
      deduped.push(plan);
    } else {
      // Keep the one with more laps (or less total time as tie-break)
      const existing = deduped.find(p => p.stops === plan.stops)!;
      if (plan.totalLaps > existing.totalLaps ||
          (plan.totalLaps === existing.totalLaps && plan.totalRaceTime < existing.totalRaceTime)) {
        deduped[deduped.indexOf(existing)] = plan;
      }
    }
  }

  if (deduped.length === 0) return null;

  // ── Step 4: Rank by total laps (most laps = best in timed race) ──
  deduped.sort((a, b) => {
    // Feasible plans always beat infeasible
    if (a.feasible && !b.feasible) return -1;
    if (!a.feasible && b.feasible) return 1;
    // Most laps wins (timed race)
    if (a.totalLaps !== b.totalLaps) return b.totalLaps - a.totalLaps;
    // Tie-break: less total race time = finished sooner / more margin
    return a.totalRaceTime - b.totalRaceTime;
  });

  const bestLaps = deduped[0].totalLaps;
  const bestTime = deduped[0].totalRaceTime;
  for (const plan of deduped) {
    plan.timeDeltaVsBest = plan.totalLaps === bestLaps
      ? plan.totalRaceTime - bestTime   // Same laps: show time delta
      : (plan.totalLaps - bestLaps) * plan.avgExpectedLapTime; // Fewer laps: estimate time cost
  }

  const recommended = deduped[0];

  // ── Step 5: Determine current stint's action ──────────────────────
  const currentStint = recommended.stints[0]; // First stint = current
  const currentAction = currentStint?.mode ?? 'normal';
  const currentTargetFuelPerLap = currentStint?.targetFuelPerLap ?? driverAvgFuelPerLap;
  const currentExpectedLapTime = currentStint?.expectedLapTime ?? driverAvgLapTime;

  // ── Step 6: Check if driver is hitting targets ────────────────────
  const targetGap = driver.cleanLaps.length >= 5
    ? detectTargetGap(
        driver.recentAvgFuelPerLap,
        driver.recentAvgLapTime,
        currentTargetFuelPerLap,
        currentExpectedLapTime,
      )
    : null;

  // If driver can't hit targets, recalculate with their actual numbers
  if (targetGap?.shouldRecalculate) {
    // Rebuild with actual performance as the new baseline
    const adjRaceTimeRemaining = lapsRemaining * driver.recentAvgLapTime;
    const adjustedPlans: StopCountPlan[] = [];
    for (const stops of uniqueStops) {
      const plan = buildPlanForStopCount(
        stops, input, adjRaceTimeRemaining,
        driver.recentAvgLapTime, driver.recentAvgFuelPerLap, driverBestLapTime,
      );
      if (plan) adjustedPlans.push(plan);
    }

    // Deduplicate adjusted plans
    const adjDeduped: StopCountPlan[] = [];
    const adjSeen = new Set<number>();
    for (const plan of adjustedPlans) {
      if (!adjSeen.has(plan.stops)) {
        adjSeen.add(plan.stops);
        adjDeduped.push(plan);
      } else {
        const existing = adjDeduped.find(p => p.stops === plan.stops)!;
        if (plan.totalLaps > existing.totalLaps ||
            (plan.totalLaps === existing.totalLaps && plan.totalRaceTime < existing.totalRaceTime)) {
          adjDeduped[adjDeduped.indexOf(existing)] = plan;
        }
      }
    }

    if (adjDeduped.length > 0) {
      adjDeduped.sort((a, b) => {
        if (a.feasible && !b.feasible) return -1;
        if (!a.feasible && b.feasible) return 1;
        if (a.totalLaps !== b.totalLaps) return b.totalLaps - a.totalLaps;
        return a.totalRaceTime - b.totalRaceTime;
      });

      const adjBestLaps = adjDeduped[0].totalLaps;
      const adjBestTime = adjDeduped[0].totalRaceTime;
      for (const p of adjDeduped) {
        p.timeDeltaVsBest = p.totalLaps === adjBestLaps
          ? p.totalRaceTime - adjBestTime
          : (p.totalLaps - adjBestLaps) * p.avgExpectedLapTime;
      }

      const adjRec = adjDeduped[0];
      const adjCurrentStint = adjRec.stints[0];

      return {
        plans: adjDeduped,
        recommended: adjRec,
        currentAction: adjCurrentStint?.mode ?? 'normal',
        currentTargetFuelPerLap: adjCurrentStint?.targetFuelPerLap ?? driverAvgFuelPerLap,
        currentExpectedLapTime: adjCurrentStint?.expectedLapTime ?? driverAvgLapTime,
        driverBestLapTime,
        driverBestFuelPerLap: driver.bestFuelPerLap,
        driverOnTarget: false,
        targetGap,
        cleanLapCount: driver.cleanLaps.length,
      };
    }
  }

  return {
    plans: deduped,
    recommended,
    currentAction,
    currentTargetFuelPerLap,
    currentExpectedLapTime,
    driverBestLapTime,
    driverBestFuelPerLap: driver.bestFuelPerLap,
    driverOnTarget: targetGap === null,
    targetGap,
    cleanLapCount: driver.cleanLaps.length,
  };
}
